import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ExternalLink, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ProductData {
  title: string;
  subtitle: string;
  category: string;
  description: string;
  features: string[];
  stack: string[];
  status: 'live' | 'beta' | 'building';
  whoFor: string;
}

const projectData: Record<string, ProductData> = {
  // === AI EMAIL (5) ===
  'lifemail': {
    title: 'lifemail',
    subtitle: 'Your Inbox, With a Memory',
    category: 'AI Email',
    description: 'An AI email client that remembers every conversation. It reads your emails, builds a relationship file for each contact, and drafts replies that match your tone. All running locally on your machine.',
    features: [
      'Connects to Gmail or Outlook via IMAP',
      'Builds memory files for every contact you email',
      'Generates reply drafts with full thread context',
      'Fast, dark UI with zero JavaScript frameworks',
      '18 end-to-end tests covering auth and sync'
    ],
    stack: ['Python', 'FastAPI', 'SQLite', 'IMAP', 'OpenAI API'],
    status: 'beta',
    whoFor: 'People who get a lot of email and are tired of staring at a blank reply box trying to remember what they last said.'
  },
  'coldmail-ai': {
    title: 'ColdMail AI',
    subtitle: 'Cold Emails That Get Replies',
    category: 'AI Email',
    description: 'Stop sending generic outreach. Paste your background and the company you want to reach. ColdMail AI writes a personalized email with a company-specific hook. Built for job seekers, sales teams, and researchers.',
    features: [
      'One-click generation with company-specific hooks',
      'Optional recipient context for sharper personalization',
      'History of every email you generated',
      'Rate limiting built in to keep usage fair',
      'Dark theme with copy-to-clipboard'
    ],
    stack: ['Python', 'FastAPI', 'Jinja2', 'OpenAI API'],
    status: 'live',
    whoFor: 'Anyone who needs to reach out cold and does not want to sound like a template. Job seekers, sales reps, researchers, founders.'
  },
  'follow-up-sequence': {
    title: 'Sequence Writer',
    subtitle: 'Never Lose a Lead to Silence',
    category: 'AI Email',
    description: 'Most deals die in the follow-up. This tool builds full sales cadences from a single brief. Hook, opener, follow-up, closer. Each email adapts based on the previous one. No more copy-paste templates that everyone ignores.',
    features: [
      'Upload a brief. Get a 5-email sequence in seconds.',
      'Each email references the previous for continuity.',
      'Tone shifts from curious to urgent as the sequence progresses.',
      'Built-in reply detection suggestions.',
      'Export to CSV or your CRM.'
    ],
    stack: ['Python', 'FastAPI', 'Pydantic', 'OpenAI API'],
    status: 'live',
    whoFor: 'Sales teams, founders doing their own outreach, recruiters, and anyone who has sent three emails and then given up.'
  },
  'subject-line-optimizer': {
    title: 'Subject Line Optimizer',
    subtitle: 'Get Opened',
    category: 'AI Email',
    description: 'The best email in the world is worthless if nobody opens it. This tool generates subject line variants, scores them by predicted open rate, and lets you A/B test before you send.',
    features: [
      'Paste your email body. Get 10 subject line variants.',
      'Each variant scored by predicted open rate.',
      'A/B test two winners against a sample list.',
      'Learns from your audience over time.',
      'Export to major email platforms.'
    ],
    stack: ['Python', 'FastAPI', 'Statistical scoring', 'OpenAI API'],
    status: 'beta',
    whoFor: 'Email marketers, sales teams, newsletter writers, and anyone whose open rate is lower than their unsubscribe rate.'
  },
  'inbox-zero-coach': {
    title: 'Inbox Zero Coach',
    subtitle: 'Clear Your Inbox Without the Anxiety',
    category: 'AI Email',
    description: 'An AI assistant that triages your inbox, suggests quick actions, and batches similar emails together so you process them in one go. No more decision fatigue on every message.',
    features: [
      'Auto-categorizes emails by action type',
      'Suggests one-sentence replies for simple requests',
      'Batches newsletters and FYIs for batch reading',
      'Flags emails that need a real response vs. ones you can ignore',
      'Daily digest of what needs your attention'
    ],
    stack: ['Python', 'FastAPI', 'IMAP', 'SQLite', 'OpenAI API'],
    status: 'building',
    whoFor: 'Anyone with 500+ unread emails who avoids opening their inbox.'
  },

  // === LEAD GENERATION (5) ===
  'linkedin-lead-scraper': {
    title: 'Lead Qualifier',
    subtitle: 'Find the Right People Without the Manual Work',
    category: 'Lead Generation',
    description: 'You know your ideal customer. This tool finds them on LinkedIn, extracts the signals that matter, and scores each lead so you know who to contact first.',
    features: [
      'Search by role, company size, industry, location.',
      'Extracts recent activity, endorsements, and job changes.',
      'Scores each lead from 0 to 100 based on fit.',
      'Exports to CSV or pushes to your CRM.',
      'Respects rate limits and terms of service.'
    ],
    stack: ['Python', 'Scrapy', 'FastAPI', 'Pandas', 'SQLite'],
    status: 'live',
    whoFor: 'B2B sales teams, recruiters, and founders who spend hours on LinkedIn instead of talking to customers.'
  },
  'local-business-finder': {
    title: 'Local Lead Finder',
    subtitle: 'Prospects in Your Backyard',
    category: 'Lead Generation',
    description: 'Finds trades and local service businesses by location and category. Extracts contact info and writes personalized outreach that sounds like you did your homework.',
    features: [
      'Search by city, suburb, or postcode plus category.',
      'Extracts phone, email, and website from listings.',
      'Generates personalized outreach referencing their actual services.',
      'Exports to CSV or your CRM.',
      'Tracks outreach history per business.'
    ],
    stack: ['Python', 'FastAPI', 'Google Maps API', 'OpenAI API'],
    status: 'live',
    whoFor: 'Local service providers, trades businesses, marketing agencies, and anyone who sells to small businesses geographically.'
  },
  'email-finder-verifier': {
    title: 'Email Verifier',
    subtitle: 'Hit the Inbox, Not the Void',
    category: 'Lead Generation',
    description: 'Guess corporate emails from a name and domain. Then verify they actually deliver before you hit send. Stop burning your sender reputation on bad addresses.',
    features: [
      'Enter a name and company domain. Get probable email formats.',
      'Verifies deliverability in real time.',
      'Detects catch-alls, role addresses, and typos.',
      'Bulk verification for list cleaning.',
      'API for integration with your existing tools.'
    ],
    stack: ['Python', 'FastAPI', 'SMTP verification', 'DNS lookup'],
    status: 'beta',
    whoFor: 'Sales teams, recruiters, and anyone who has ever had an email bounce back with "user unknown."'
  },
  'competitor-monitor': {
    title: 'Competitor Monitor',
    subtitle: 'Know What They Are Doing Before Your Customers Do',
    category: 'Lead Generation',
    description: 'Tracks competitor websites, pricing pages, and job boards for changes. Alerts you when they launch a feature, change pricing, or hire for a new role.',
    features: [
      'Monitors any public URL on a schedule you set',
      'Detects text, image, and pricing changes',
      'Summarizes changes in plain English',
      'Alerts via email or webhook',
      'Historical timeline of every change detected'
    ],
    stack: ['Python', 'FastAPI', 'Playwright', 'SQLite', 'Diff engine'],
    status: 'building',
    whoFor: 'Product managers, competitive intelligence teams, and founders who want to know the market before making moves.'
  },
  'review-response-generator': {
    title: 'Review Response Generator',
    subtitle: 'Reply to Every Review in Under 10 Seconds',
    category: 'Lead Generation',
    description: 'Turns Google and Yelp reviews into professional, on-brand responses. Never let a bad review sit unanswered because you do not know what to say.',
    features: [
      'Paste a review. Get 3 response options: grateful, neutral, assertive.',
      'Matches your brand voice from sample responses.',
      'Flags reviews that need escalation beyond a reply.',
      'Batch-generate responses for multiple reviews.',
      'One-click copy for posting.'
    ],
    stack: ['Python', 'FastAPI', 'OpenAI API'],
    status: 'building',
    whoFor: 'Restaurant owners, service businesses, and anyone who gets reviews and struggles to respond consistently.'
  },

  // === CONTENT (4) ===
  'blog-post-generator': {
    title: 'Blog Post Generator',
    subtitle: 'Long-Form Content from Briefs',
    category: 'Content',
    description: 'Turn a one-paragraph brief into a full SEO-optimized blog post. Headers, body, meta description, the lot. You edit. It writes the first draft so you are not staring at a blank page.',
    features: [
      'Paste a brief or bullet points. Get a full draft.',
      'Auto-generates H2s and H3s based on topic structure.',
      'Writes meta descriptions and social snippets.',
      'Optimizes for target keywords without stuffing.',
      'Export to Markdown, HTML, or publish directly.'
    ],
    stack: ['Python', 'FastAPI', 'SEO analysis', 'OpenAI API'],
    status: 'beta',
    whoFor: 'Content marketers, SEO agencies, founders who write their own blog, and anyone who needs more content than they have hours.'
  },
  'linkedin-post-writer': {
    title: 'LinkedIn Post Writer',
    subtitle: 'Thought Leadership Without the Writer',
    category: 'Content',
    description: 'Executive ghostwriting for LinkedIn. Paste a few bullet points or a rough idea. Get a polished post that sounds like you spent an hour on it.',
    features: [
      'Paste bullet points or a voice memo transcript.',
      'Generates a post with hook, story, and CTA.',
      'Matches your tone from previous posts.',
      'Suggests hashtags and optimal posting time.',
      'Schedules or publishes directly to LinkedIn.'
    ],
    stack: ['Python', 'FastAPI', 'LinkedIn API', 'OpenAI API'],
    status: 'beta',
    whoFor: 'Executives, founders, consultants, and anyone who knows they should post more on LinkedIn but never finds the time.'
  },
  'newsletter-builder': {
    title: 'Newsletter Builder',
    subtitle: 'Curate and Write Newsletters from RSS + Notes',
    category: 'Content',
    description: 'Combines your RSS feeds, bookmarks, and rough notes into a structured newsletter draft. You add commentary. It handles the structure, formatting, and send-ready HTML.',
    features: [
      'Pulls articles from RSS feeds you configure',
      'Summarizes each article in your voice',
      'Organizes picks into intro, main story, quick links',
      'Generates HTML ready for any email platform',
      'Scheduling and archive management'
    ],
    stack: ['Python', 'FastAPI', 'RSS parser', 'Jinja2', 'OpenAI API'],
    status: 'building',
    whoFor: 'Newsletter writers, content curators, and anyone who wants to send a weekly digest without the weekly grind.'
  },
  'faq-builder': {
    title: 'FAQ Builder',
    subtitle: 'Turn Support Tickets into a Self-Service Knowledge Base',
    category: 'Content',
    description: 'Reads your support tickets, chat logs, and emails. Extracts the recurring questions and writes clear FAQ entries with answers. Reduces support volume without hiring.',
    features: [
      'Ingests tickets from Zendesk, Intercom, or CSV export',
      'Clusters questions by semantic similarity',
      'Drafts answers in your support tone',
      'Ranks FAQs by frequency and impact',
      'Exports to Markdown, Notion, or your help center'
    ],
    stack: ['Python', 'FastAPI', 'Embedding clustering', 'OpenAI API'],
    status: 'building',
    whoFor: 'Support teams, SaaS founders, and anyone answering the same questions twenty times a week.'
  },

  // === DOCUMENTS (4) ===
  'contract-scanner': {
    title: 'Contract Scanner',
    subtitle: 'Flag Risky Clauses Before You Sign',
    category: 'Documents',
    description: 'Upload a contract. It highlights clauses that are unusually one-sided, missing standard protections, or written in language that could be interpreted against you. Not legal advice. Just a second pair of eyes.',
    features: [
      'Upload PDF or DOCX contracts',
      'Flags unusual liability, termination, and IP clauses',
      'Compares against standard contract templates',
      'Explains each flag in plain English',
      'Export annotated contract with highlights'
    ],
    stack: ['Python', 'FastAPI', 'PDF parser', 'OpenAI API'],
    status: 'beta',
    whoFor: 'Founders, freelancers, and small business owners who sign contracts without a lawyer on retainer.'
  },
  'resume-parser': {
    title: 'Resume Parser',
    subtitle: 'Extract Skills and Experience in Seconds',
    category: 'Documents',
    description: 'Drop in a resume or CV. It extracts structured data: skills, experience timeline, education, certifications. Works with any format, even scanned PDFs. Perfect for building candidate databases.',
    features: [
      'Handles PDF, DOCX, and image uploads',
      'Extracts structured data: skills, roles, dates, education',
      'Scores candidate fit against a job description',
      'Exports to JSON, CSV, or your ATS',
      'Batch processing for high-volume hiring'
    ],
    stack: ['Python', 'FastAPI', 'OCR', 'Pydantic', 'OpenAI API'],
    status: 'live',
    whoFor: 'Recruiters, hiring managers, and anyone who reads more than ten resumes a week.'
  },
  'proposal-writer': {
    title: 'Proposal Writer',
    subtitle: 'Win More Projects with Better Proposals',
    category: 'Documents',
    description: 'Turn a client brief and your capabilities into a professional proposal. Scope, timeline, pricing, and terms. Structured, persuasive, and ready to send.',
    features: [
      'Paste client requirements. Get a full proposal draft.',
      'Auto-generates scope, timeline, and pricing sections',
      'Adapts tone based on client industry',
      'Includes standard terms and conditions placeholders',
      'Export to PDF or DOCX'
    ],
    stack: ['Python', 'FastAPI', 'Jinja2', 'OpenAI API'],
    status: 'building',
    whoFor: 'Agencies, consultants, freelancers, and anyone who writes project proposals regularly.'
  },
  'pdf-summarizer': {
    title: 'PDF Summarizer',
    subtitle: 'Understand Any Document in 60 Seconds',
    category: 'Documents',
    description: 'Upload a long PDF — research paper, annual report, legal filing. Get a structured summary with key findings, risks, and action items. No more reading 50 pages for one data point.',
    features: [
      'Handles PDFs up to 500 pages',
      'Extracts key findings, risks, and action items',
      'Answers follow-up questions about the document',
      'Compares two documents for differences',
      'Exports summary to Markdown or Notion'
    ],
    stack: ['Python', 'FastAPI', 'PDF parser', 'Chunked RAG', 'OpenAI API'],
    status: 'beta',
    whoFor: 'Researchers, investors, lawyers, and anyone who needs to digest long documents quickly.'
  },

  // === VOICE / MEETING (2) ===
  'meeting-notes-ai': {
    title: 'Meeting Notes AI',
    subtitle: 'Transcribe, Summarize, and Track Action Items',
    category: 'Voice & Meeting',
    description: 'Records or uploads meeting audio. Transcribes with speaker labels, summarizes decisions, and extracts action items with owners and deadlines. No more "what did we agree on?"',
    features: [
      'Upload audio or record directly in browser',
      'Speaker diarization for multi-person meetings',
      'Auto-summarizes decisions and open questions',
      'Extracts action items with suggested owners',
      'Exports to Notion, Slack, or your task manager'
    ],
    stack: ['Python', 'FastAPI', 'Whisper', 'SQLite', 'OpenAI API'],
    status: 'beta',
    whoFor: 'Project managers, team leads, and anyone who runs meetings and needs to share what happened.'
  },
  'voice-memo-transcriber': {
    title: 'Voice Memo Transcriber',
    subtitle: 'Turn Voice Notes into Structured Text',
    category: 'Voice & Meeting',
    description: 'Record a voice memo while walking or driving. Get a clean transcript organized into topics, tasks, and ideas. Your random thoughts become actionable notes.',
    features: [
      'Upload voice memos from any device',
      'Transcribes with high accuracy even with background noise',
      'Auto-tags topics, tasks, and ideas',
      'Integrates with your note-taking app',
      'Search across all your voice memos by content'
    ],
    stack: ['Python', 'FastAPI', 'Whisper', 'SQLite'],
    status: 'building',
    whoFor: 'Journalists, researchers, founders, and anyone who thinks better out loud than at a keyboard.'
  },

  // === SOCIAL (2) ===
  'social-media-scheduler': {
    title: 'Social Media Scheduler',
    subtitle: 'Queue, Schedule, and Auto-Post Across Platforms',
    category: 'Social',
    description: 'Write once, post everywhere. Queues your content, suggests optimal times, and publishes to Twitter, LinkedIn, and Instagram. With analytics on what actually performs.',
    features: [
      'Draft posts with preview for each platform',
      'Suggests best posting time based on engagement history',
      'Auto-resizes images for each platform',
      'Queues content so you batch-create once a week',
      'Analytics: views, engagement, follower growth'
    ],
    stack: ['Python', 'FastAPI', 'Social APIs', 'SQLite', 'Celery'],
    status: 'building',
    whoFor: 'Content creators, social media managers, and founders who want a social presence without daily posting.'
  },
  'hashtag-researcher': {
    title: 'Hashtag Researcher',
    subtitle: 'Find Hashtags That Actually Reach People',
    category: 'Social',
    description: 'Analyzes hashtag performance, competition, and audience overlap. Suggests tags that put your content in front of the right people, not just the most crowded hashtags.',
    features: [
      'Enter a topic. Get ranked hashtag suggestions.',
      'Shows competition level and estimated reach for each tag',
      'Suggests hashtag sets for different post types',
      'Tracks which hashtags drove your best-performing posts',
      'Exports to clipboard or your social scheduler'
    ],
    stack: ['Python', 'FastAPI', 'Social APIs', 'Analytics'],
    status: 'building',
    whoFor: 'Instagram creators, TikTok marketers, and anyone who suspects #love and #instagood are not helping.'
  },

  // === OPERATIONS / DATA (4) ===
  'data-cleaner': {
    title: 'Data Cleaner',
    subtitle: 'Fix CSVs, Dedupe, and Standardize in One Pass',
    category: 'Operations',
    description: 'Upload a messy CSV. It detects duplicates, fixes formatting inconsistencies, fills missing values where possible, and standardizes columns. The data you actually get from clients, cleaned.',
    features: [
      'Upload any CSV or Excel file',
      'Auto-detects duplicates with fuzzy matching',
      'Standardizes phone numbers, addresses, and names',
      'Suggests fills for missing values',
      'Exports clean data with a before/after report'
    ],
    stack: ['Python', 'FastAPI', 'Pandas', 'Fuzzy matching', 'OpenAI API'],
    status: 'live',
    whoFor: 'Data analysts, CRM managers, and anyone who has ever opened a client CSV and wanted to cry.'
  },
  'invoice-generator': {
    title: 'Invoice Generator',
    subtitle: 'From Time Entries to Professional Invoice in 30 Seconds',
    category: 'Operations',
    description: 'Paste your time entries or project milestones. Generates a professional invoice with line items, tax calculations, and payment terms. Sends via email or exports to PDF.',
    features: [
      'Paste time entries or upload a timesheet',
      'Auto-generates line items with rates and descriptions',
      'Calculates tax, discounts, and totals',
      'Branded template with your logo and colors',
      'Email directly or export to PDF / accounting software'
    ],
    stack: ['Python', 'FastAPI', 'Jinja2', 'PDF generation', 'Email'],
    status: 'live',
    whoFor: 'Freelancers, agencies, and consultants who bill by the hour or milestone.'
  },
  'api-tester': {
    title: 'API Tester',
    subtitle: 'Generate Test Cases and Catch Edge Cases Automatically',
    category: 'Operations',
    description: 'Point it at your API spec. It generates test cases for happy paths, edge cases, and failure modes. Runs them and reports what broke. Catches the bugs you would have found in production.',
    features: [
      'Import from OpenAPI spec or Postman collection',
      'Generates tests for all endpoints automatically',
      'Tests edge cases: empty input, max lengths, special chars',
      'Runs load tests with configurable concurrency',
      'CI/CD integration with GitHub Actions'
    ],
    stack: ['Python', 'FastAPI', 'Hypothesis', 'Pytest', 'Docker'],
    status: 'beta',
    whoFor: 'Backend developers, QA engineers, and anyone shipping APIs without a dedicated test team.'
  },
  'feature-request-prioritizer': {
    title: 'Feature Request Prioritizer',
    subtitle: 'Decide What to Build Next with Data, Not Gut Feel',
    category: 'Operations',
    description: 'Collects feature requests from support tickets, surveys, and user interviews. Clusters them by theme, scores by impact and effort, and ranks what to build next. No more building features nobody uses.',
    features: [
      'Ingests requests from multiple sources',
      'Clusters by semantic similarity to find themes',
      'Scores by user impact, technical effort, and strategic fit',
      'Generates a ranked roadmap with justification',
      'Tracks which shipped features moved the needle'
    ],
    stack: ['Python', 'FastAPI', 'Embedding clustering', 'SQLite', 'OpenAI API'],
    status: 'building',
    whoFor: 'Product managers, founders, and anyone who gets feature requests in Slack, email, and support tickets all at once.'
  },

  // === NICHE (2) ===
  'onboarding-email-sequence': {
    title: 'Onboarding Email Sequence',
    subtitle: 'Turn Trial Users into Paying Customers',
    category: 'Niche',
    description: 'Builds a personalized onboarding email sequence for your SaaS. Each email is timed to where the user is in their journey. Activates dormant users and reduces churn before it starts.',
    features: [
      'Connects to your user database or segment tool',
      'Generates emails based on user behavior triggers',
      'A/B tests subject lines and send times',
      'Tracks activation milestones per user cohort',
      'Reduces churn with re-engagement sequences'
    ],
    stack: ['Python', 'FastAPI', 'Segment integration', 'OpenAI API'],
    status: 'building',
    whoFor: 'SaaS founders, growth teams, and anyone who watches trial users sign up and then disappear.'
  },
  'churn-prediction-dashboard': {
    title: 'Churn Prediction Dashboard',
    subtitle: 'See Who Is Leaving Before They Cancel',
    category: 'Niche',
    description: 'Analyzes user behavior patterns to predict churn risk. Flags at-risk accounts with specific reasons and suggested retention actions. Stop reacting to cancellations. Start preventing them.',
    features: [
      'Connects to your product analytics or database',
      'Scores each account by churn risk (0-100)',
      'Explains why the account is at risk',
      'Suggests specific retention actions per account',
      'Weekly digest of at-risk accounts and outcomes'
    ],
    stack: ['Python', 'FastAPI', 'Scikit-learn', 'Pandas', 'SQLite'],
    status: 'building',
    whoFor: 'SaaS founders, customer success teams, and anyone who gets surprised by cancellation emails.'
  }
};

const statusBadge = (status: string) => {
  const map: Record<string, { text: string; className: string }> = {
    live: { text: 'Live', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    beta: { text: 'Beta', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    building: { text: 'Building', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
  };
  const s = map[status] || map.building;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.className}`}>
      {s.text}
    </span>
  );
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const project = projectData[projectId || ''];

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col bg-dark-500">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Product Not Found</h1>
            <p className="text-gray-300 mb-8">Check back. New products drop regularly.</p>
            <Link to="/projects" className="text-tasktide-teal hover:text-tasktide-blue transition-colors">
              ← Back to Products
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-dark-500">
      <Navbar />
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          
          <div className="mb-2">
            <Link to="/projects" className="text-sm text-gray-400 hover:text-tasktide-teal transition-colors flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> All Products
            </Link>
          </div>
          
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-tasktide-teal font-medium">{project.category}</span>
              {statusBadge(project.status)}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-3">{project.title}</h1>
            <p className="text-xl text-tasktide-teal mb-6">{project.subtitle}</p>
            <p className="text-gray-300 text-lg leading-relaxed">{project.description}</p>
          </div>
          
          <div className="glass-card p-8 rounded-xl shadow-lg mb-10 border border-white/5">
            <h3 className="text-lg font-medium text-tasktide-teal mb-6">What It Does</h3>
            <ul className="space-y-4 text-gray-300">
              {project.features.map((f: string, i: number) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-tasktide-teal mt-1.5 h-1.5 w-1.5 rounded-full bg-tasktide-teal flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <h3 className="text-lg font-medium text-tasktide-purple mb-3">Who It Is For</h3>
              <p className="text-gray-300">{project.whoFor}</p>
            </div>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <h3 className="text-lg font-medium text-tasktide-orange mb-3">Tech Stack</h3>
              <div className="flex flex-wrap gap-2">
                {project.stack.map((s: string) => (
                  <span key={s} className="px-2 py-1 rounded-md bg-dark-300/80 text-gray-300 text-sm border border-white/5">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          <div className="glass-card p-6 rounded-xl border border-white/5 mb-10">
            <h3 className="text-lg font-medium text-white mb-3">Interested?</h3>
            <p className="text-gray-300 mb-4">
              Every product ships with source code, tests, and documentation. 
              Want early access, a custom build, or just want to talk about what we are working on next?
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-gradient-to-r from-tasktide-teal to-tasktide-blue hover:shadow-lg hover:shadow-tasktide-teal/20 text-white">
                <a href="mailto:info@tasktide.com.au">
                  <Mail className="h-4 w-4 mr-2" /> Get in Touch
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white/10 text-white hover:bg-dark-300/50 hover:text-tasktide-teal">
                <Link to="/projects">
                  <ExternalLink className="h-4 w-4 mr-2" /> Browse More Products
                </Link>
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/projects" className="text-tasktide-teal hover:text-tasktide-blue transition-colors text-sm">
              ← Back to Products
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectDetail;